import React, { useState } from "react";
import { Play, History, ArrowLeft } from "lucide-react";
import { useAppSelector } from "@/redux/hooks/hooks";
import {
  useAddVideoAnalysisMutation,
  useGetVideoAnalysisQuery,
} from "@/redux/feature/videoDashboard/VideoDashboardApi";
import LoadingState from "./components/LoadingState";
import ErrorState from "./components/ErrorState";
import ResultsDisplay from "./components/ResultsDisplay";
import PreviousAnalysisList from "./components/PreviousAnalysisList";
import FileUploadField from "@/components/ui/FileUploadField";

interface VideoAnalysisModalProps {
  setIsOpen: (isOpen: boolean) => void;
}

const VideoAnalysisModal: React.FC<VideoAnalysisModalProps> = ({
  setIsOpen,
}) => {
  const [videoUrl, setVideoUrl] = useState("");
  const [taskId, setTaskId] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPreviousAnalysis, setShowPreviousAnalysis] = useState(false);
  const [viewingFromList, setViewingFromList] = useState(false);
  const [filesUploaded, setFilesUploaded] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);

  const { user } = useAppSelector((state) => state.auth);
  const [addVideoAnalysis] = useAddVideoAnalysisMutation();
  const { data: analysisData } = useGetVideoAnalysisQuery(
    { taskID: taskId },
    { skip: !taskId || !isAnalyzing, pollingInterval: 3000 }
  );

  // Handle video upload
  const handleVideoUpload = (files: any[]) => {
    if (files.length > 0 && files[0].live_url) {
      setVideoUrl(files[0].live_url);
      setFilesUploaded(true);
      setResult(null);
      setError(null);
    } else {
      setVideoUrl("");
      setFilesUploaded(false);
    }
  };

  // Start analysis
  const startAnalysis = async () => {
    if (!videoUrl || !filesUploaded) {
      setError("Please upload a video first");
      return;
    }

    try {
      setError(null);
      setIsAnalyzing(true);

      const response = await addVideoAnalysis({
        email: user?.email,
        videoUrl,
      }).unwrap();

      if (response.task_id) {
        setTaskId(response.task_id);
      } else {
        throw new Error("Failed to start analysis");
      }
    } catch (err: any) {
      setError(err.message || "Analysis failed");
      setIsAnalyzing(false);
    }
  };

  // Handle polling response
  React.useEffect(() => {
    if (!analysisData || !isAnalyzing) return;

    if (
      analysisData.status === "SUCCESS" ||
      analysisData.status === "completed"
    ) {
      setResult(analysisData.result);
      setIsAnalyzing(false);
    } else if (analysisData.status === "failed") {
      setError(analysisData.error || "Analysis failed");
      setIsAnalyzing(false);
    }
  }, [analysisData, isAnalyzing]);

  // Handle previous analysis selection
  const handleSelectPreviousAnalysis = (analysis: any) => {
    if (analysis.result) {
      setResult(analysis.result);
      setTaskId(analysis.video_url);
      setViewingFromList(true); // Set this flag
      setShowPreviousAnalysis(false);
    } else {
      setError("No analysis results found");
    }
    setResult(analysis.result);
    setShowPreviousAnalysis(false);
  };

  // Reset to initial state
  const resetAnalysis = () => {
    setVideoUrl("");
    setTaskId(null);
    setResult(null);
    setError(null);
    setViewingFromList(false);
    setFilesUploaded(false);
    setUploadKey((prev) => prev + 1);
  };

  // Render states
  if (error) {
    return (
      <ErrorState
        message={error}
        onRetry={resetAnalysis}
        onClose={() => setIsOpen(false)}
      />
    );
  }

  if (isAnalyzing) {
    return <LoadingState onCancel={() => setIsAnalyzing(false)} />;
  }

  if (result) {
    return (
      <ResultsDisplay
        result={result}
        taskId={taskId || undefined}
        onNewAnalysis={resetAnalysis}
        onClose={() => setIsOpen(false)}
        showBackToList={viewingFromList}
        onBackToList={() => {
          setViewingFromList(false);
          setShowPreviousAnalysis(true);
          setResult(null);
        }}
      />
    );
  }

  if (showPreviousAnalysis) {
    return (
      <PreviousAnalysisList
        onBack={() => setShowPreviousAnalysis(false)}
        onSelectAnalysis={handleSelectPreviousAnalysis}
      />
    );
  }

  // Main view
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
          Video Analysis
        </h2>
        <button
          onClick={() => setShowPreviousAnalysis(true)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-gray-400 rounded"
        >
          <History className="w-4 h-4" />
          Previous Analyses
        </button>
      </div>

      <div className="space-y-4">
        <FileUploadField
          onFilesUploaded={handleVideoUpload}
          maxFiles={1}
          maxSize={500 * 1024 * 1024}
          accept={{
            "video/*": [".mp4", ".mov", ".avi", ".wmv", ".webm"],
          }}
          identifier="video-analysis"
          key={`upload-${uploadKey}`} // Use the key to force remount
          clearFiles={result !== null} // Clear when there's a previous result
        />

        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            onClick={() => setIsOpen(false)}
            className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100 transition"
          >
            Cancel
          </button>
          <button
            onClick={startAnalysis}
            disabled={!videoUrl}
            className={`flex-1 px-6 py-3 rounded-lg flex items-center justify-center gap-2 text-sm font-medium transition ${
              videoUrl
                ? "bg-gray-900 text-white hover:bg-gray-800"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            <Play className="w-4 h-4" />
            Start Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoAnalysisModal;
